import { useEffect, useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Envelope, CheckCircle, Trash, Eye, EyeSlash, Lightning, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import VendorMappingsCard from "@/components/VendorMappingsCard";

const fetcher = (url) => api.get(url).then((r) => r.data);

const PRESET_SERVERS = [
    { label: "Outlook / Microsoft 365", server: "outlook.office365.com", port: 993, ssl: true },
    { label: "Gmail", server: "imap.gmail.com", port: 993, ssl: true },
    { label: "OVH Mail", server: "ssl0.ovh.net", port: 993, ssl: true },
    { label: "Free", server: "imap.free.fr", port: 993, ssl: true },
    { label: "Orange", server: "imap.orange.fr", port: 993, ssl: true },
    { label: "iCloud", server: "imap.mail.me.com", port: 993, ssl: true },
];

function ImapConfigForm({ divers, mutate, onConfigured }) {
    const [email, setEmail] = useState(divers.email || "");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [server, setServer] = useState(divers.imap_server || "");
    const [port, setPort] = useState(divers.imap_port || 993);
    const [ssl, setSsl] = useState(divers.imap_use_ssl !== false);
    const [folder, setFolder] = useState(divers.folder || "INBOX");
    const [processedFolder, setProcessedFolder] = useState(divers.processed_folder || "");
    const [processedFolderBl, setProcessedFolderBl] = useState(divers.processed_folder_bl || "");
    const [processedFolderBi, setProcessedFolderBi] = useState(divers.processed_folder_bi || "");
    const [processedFolderDevisAccepte, setProcessedFolderDevisAccepte] = useState(divers.processed_folder_devis_accepte || "");
    const [untreatedFolder, setUntreatedFolder] = useState(divers.untreated_folder || "");
    const [folders, setFolders] = useState([]);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);

    // If creds are already saved, fetch the folder list at mount using the
    // stored password — so both source & destination dropdowns are populated
    // without forcing the user to re-enter their app password.
    useEffect(() => {
        if (!divers.configured) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/divers/folders-saved");
                if (!cancelled) setFolders(data.folders || []);
            } catch (e) {
                // Silent — user can still click "Lister les dossiers" manually
            }
        })();
        return () => { cancelled = true; };
    }, [divers.configured]);

    const applyPreset = (preset) => {
        setServer(preset.server);
        setPort(preset.port);
        setSsl(preset.ssl);
    };

    const testConnection = async () => {
        if (!email || !password || !server) {
            toast.error("Saisis l'email, le mot de passe et le serveur d'abord");
            return;
        }
        setTesting(true);
        try {
            const { data } = await api.post("/divers/folders", {
                email, password,
                imap_server: server,
                imap_port: parseInt(port, 10) || 993,
                imap_use_ssl: ssl,
            });
            setFolders(data.folders || []);
            toast.success(`${(data.folders || []).length} dossiers trouvés`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Connexion échouée");
        } finally {
            setTesting(false);
        }
    };

    const save = async () => {
        if (!email || (!password && !divers.configured) || !server) {
            toast.error("Email, mot de passe et serveur requis");
            return;
        }
        setSaving(true);
        try {
            await api.put("/credentials/divers", {
                email,
                ...(password ? { password } : {}),
                imap_server: server,
                imap_port: parseInt(port, 10) || 993,
                imap_use_ssl: ssl,
                folder: folder || "INBOX",
                processed_folder: processedFolder || null,
                processed_folder_bl: processedFolderBl || null,
                processed_folder_bi: processedFolderBi || null,
                processed_folder_devis_accepte: processedFolderDevisAccepte || null,
                untreated_folder: untreatedFolder || null,
            });
            toast.success("Connecteur mail enregistré");
            setPassword("");
            mutate();
            onConfigured?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm("Supprimer la configuration mail ?")) return;
        await api.delete("/credentials/divers");
        toast.success("Configuration supprimée");
        setPassword("");
        setFolders([]);
        mutate();
    };

    return (
        <Card className="ec-card" data-testid="imap-config-card">
            <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                            <Envelope size={18} weight="duotone" />
                            Configuration IMAP
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Connecte ta boîte mail. Identifiants stockés chiffrés (AES).
                        </p>
                    </div>
                    {divers.configured && (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-200 text-xs">
                            <CheckCircle size={12} weight="fill" className="mr-1" />
                            Configuré
                        </Badge>
                    )}
                </div>

                {/* Presets */}
                <div>
                    <Label className="text-xs">Préréglages</Label>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                        {PRESET_SERVERS.map((p) => (
                            <Button
                                key={p.server}
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => applyPreset(p)}
                                data-testid={`imap-preset-${p.server}`}
                            >
                                {p.label}
                            </Button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="imap-email">Adresse email</Label>
                        <Input
                            id="imap-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="vous@exemple.com"
                            data-testid="imap-email"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="imap-pwd">Mot de passe d&apos;application</Label>
                        <div className="relative">
                            <Input
                                id="imap-pwd"
                                type={show ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder={divers.configured ? "•••••• (ressaisir pour modifier)" : "••••••"}
                                autoComplete="new-password"
                                data-testid="imap-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShow((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                            >
                                {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Gmail/Outlook → génère un mot de passe d&apos;application dans la sécurité de ton compte.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_140px] gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="imap-server">Serveur IMAP</Label>
                        <Input
                            id="imap-server"
                            value={server}
                            onChange={(e) => setServer(e.target.value)}
                            placeholder="imap.exemple.com"
                            data-testid="imap-server"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="imap-port">Port</Label>
                        <Input
                            id="imap-port"
                            type="number"
                            value={port}
                            onChange={(e) => setPort(e.target.value)}
                            data-testid="imap-port"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="imap-ssl">SSL/TLS</Label>
                        <Select value={ssl ? "yes" : "no"} onValueChange={(v) => setSsl(v === "yes")}>
                            <SelectTrigger id="imap-ssl" data-testid="imap-ssl">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="yes">SSL</SelectItem>
                                <SelectItem value="no">Aucun</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="imap-folder">Dossier à scanner</Label>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={testConnection}
                            disabled={testing}
                            data-testid="imap-test"
                        >
                            {testing ? "Connexion…" : "Lister les dossiers"}
                        </Button>
                    </div>
                    {folders.length > 0 ? (
                        <Select value={folder} onValueChange={setFolder}>
                            <SelectTrigger id="imap-folder" data-testid="imap-folder">
                                <SelectValue placeholder="Choisis un dossier" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {folders.map((f) => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            id="imap-folder"
                            value={folder}
                            onChange={(e) => setFolder(e.target.value)}
                            placeholder="INBOX (clique sur 'Lister' pour voir tes dossiers)"
                            data-testid="imap-folder-input"
                        />
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="imap-processed-folder">
                        Dossier Factures (et avoirs)
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (optionnel — mails contenant une facture ou un avoir ; sert aussi de repli pour les autres types ci-dessous si vide)
                        </span>
                    </Label>
                    {folders.length > 0 ? (
                        <Select
                            value={processedFolder || "__none__"}
                            onValueChange={(v) => setProcessedFolder(v === "__none__" ? "" : v)}
                        >
                            <SelectTrigger id="imap-processed-folder" data-testid="imap-processed-folder">
                                <SelectValue placeholder="Aucun déplacement" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__none__">— Ne pas déplacer —</SelectItem>
                                {folders.filter((f) => f !== folder).map((f) => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            id="imap-processed-folder"
                            value={processedFolder}
                            onChange={(e) => setProcessedFolder(e.target.value)}
                            placeholder="ex: TERMINE (laisser vide pour ne pas déplacer)"
                            data-testid="imap-processed-folder-input"
                        />
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="imap-folder-bl">
                        Dossier Bons de livraison
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (optionnel — vide = même que « Factures »)
                        </span>
                    </Label>
                    {folders.length > 0 ? (
                        <Select
                            value={processedFolderBl || "__none__"}
                            onValueChange={(v) => setProcessedFolderBl(v === "__none__" ? "" : v)}
                        >
                            <SelectTrigger id="imap-folder-bl" data-testid="imap-folder-bl">
                                <SelectValue placeholder="Même dossier que Factures" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__none__">— Même dossier que Factures —</SelectItem>
                                {folders.filter((f) => f !== folder).map((f) => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            id="imap-folder-bl"
                            value={processedFolderBl}
                            onChange={(e) => setProcessedFolderBl(e.target.value)}
                            placeholder="ex: BONS LIVRAISON (vide = même dossier que Factures)"
                            data-testid="imap-folder-bl-input"
                        />
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="imap-folder-bi">
                        Dossier Bons d&apos;intervention
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (optionnel — vide = même que « Factures »)
                        </span>
                    </Label>
                    {folders.length > 0 ? (
                        <Select
                            value={processedFolderBi || "__none__"}
                            onValueChange={(v) => setProcessedFolderBi(v === "__none__" ? "" : v)}
                        >
                            <SelectTrigger id="imap-folder-bi" data-testid="imap-folder-bi">
                                <SelectValue placeholder="Même dossier que Factures" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__none__">— Même dossier que Factures —</SelectItem>
                                {folders.filter((f) => f !== folder).map((f) => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            id="imap-folder-bi"
                            value={processedFolderBi}
                            onChange={(e) => setProcessedFolderBi(e.target.value)}
                            placeholder="ex: BONS INTERVENTION (vide = même dossier que Factures)"
                            data-testid="imap-folder-bi-input"
                        />
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="imap-folder-devis-accepte">
                        Dossier Devis acceptés
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (optionnel — vide = même que « Factures »)
                        </span>
                    </Label>
                    {folders.length > 0 ? (
                        <Select
                            value={processedFolderDevisAccepte || "__none__"}
                            onValueChange={(v) => setProcessedFolderDevisAccepte(v === "__none__" ? "" : v)}
                        >
                            <SelectTrigger id="imap-folder-devis-accepte" data-testid="imap-folder-devis-accepte">
                                <SelectValue placeholder="Même dossier que Factures" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__none__">— Même dossier que Factures —</SelectItem>
                                {folders.filter((f) => f !== folder).map((f) => (
                                    <SelectItem key={f} value={f}>{f}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            id="imap-folder-devis-accepte"
                            value={processedFolderDevisAccepte}
                            onChange={(e) => setProcessedFolderDevisAccepte(e.target.value)}
                            placeholder="ex: DEVIS ACCEPTES (vide = même dossier que Factures)"
                            data-testid="imap-folder-devis-accepte-input"
                        />
                    )}
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="imap-untreated-folder">
                        Dossier mails non traités
                        <span className="ml-2 text-xs text-muted-foreground font-normal">
                            (optionnel — les mails sans facture PDF iront ici pour relecture manuelle)
                        </span>
                    </Label>
                    {folders.length > 0 ? (
                        <Select
                            value={untreatedFolder || "__none__"}
                            onValueChange={(v) => setUntreatedFolder(v === "__none__" ? "" : v)}
                        >
                            <SelectTrigger id="imap-untreated-folder" data-testid="imap-untreated-folder">
                                <SelectValue placeholder="Aucun déplacement" />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                <SelectItem value="__none__">— Ne pas déplacer —</SelectItem>
                                {folders
                                    .filter((f) => f !== folder && f !== processedFolder)
                                    .map((f) => (
                                        <SelectItem key={f} value={f}>{f}</SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            id="imap-untreated-folder"
                            value={untreatedFolder}
                            onChange={(e) => setUntreatedFolder(e.target.value)}
                            placeholder="ex: NON TRAITE (laisser vide pour ne pas déplacer)"
                            data-testid="imap-untreated-folder-input"
                        />
                    )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                    {divers.configured && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={remove}
                            className="text-destructive hover:bg-destructive/10"
                            data-testid="imap-delete"
                        >
                            <Trash size={14} className="mr-1" />
                            Supprimer
                        </Button>
                    )}
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="imap-save"
                    >
                        {saving ? "Enregistrement…" : "Enregistrer"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}

function ImapConfigCard({ onConfigured }) {
    const { data, mutate } = useSWR("/credentials", fetcher);
    if (!data) {
        return (
            <Card className="ec-card">
                <CardContent className="p-6 text-sm text-muted-foreground">Chargement…</CardContent>
            </Card>
        );
    }
    const divers = (data.items || []).find((i) => i.provider === "divers") || { configured: false };
    // `key` forces a fresh form (with new initial state) after each save.
    const k = `${divers.email || ""}-${divers.imap_server || ""}-${divers.updated_at || ""}`;
    return <ImapConfigForm key={k} divers={divers} mutate={mutate} onConfigured={onConfigured} />;
}


function ImportRunner() {
    const { data, mutate } = useSWR("/credentials", fetcher);
    const divers = (data?.items || []).find((i) => i.provider === "divers");
    const configured = divers?.configured;

    const [running, setRunning] = useState(false);
    const [status, setStatus] = useState(null);
    const [testing, setTesting] = useState(false);
    const [probe, setProbe] = useState(null);

    const testRead = async () => {
        setTesting(true);
        setProbe(null);
        try {
            const { data } = await api.post("/divers/probe", { folder: divers?.folder || "INBOX" });
            setProbe(data);
            toast.success(`${data.total} mails dans « ${divers?.folder || "INBOX"} » — ${data.pdf_count_in_sample} PDF dans les ${data.sample_size} derniers`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec du test");
        } finally {
            setTesting(false);
        }
    };

    const runImport = async () => {
        setRunning(true);
        setStatus(null);
        try {
            const { data } = await api.post("/divers/harvest", {});
            toast.info("Import lancé");
            setStatus({ id: data.id, message: data.message, progress: data.progress });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec du lancement");
            setRunning(false);
        }
    };

    // Poll status while job running
    useEffect(() => {
        if (!status?.id) return;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try {
                const { data } = await api.get("/sync/current");
                if (data) {
                    setStatus(data);
                    if (data.status === "success" || data.status === "failed") {
                        setRunning(false);
                        mutate();
                        return;
                    }
                }
            } catch (e) {
                // ignore
            }
            setTimeout(poll, 2500);
        };
        poll();
        return () => { cancelled = true; };
    }, [status?.id, mutate]);

    return (
        <Card className="ec-card" data-testid="import-runner-card">
            <CardContent className="p-6 space-y-4">
                <div>
                    <h3 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
                        <Lightning size={18} weight="duotone" />
                        Lancer l&apos;import
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Récupère les PDF en pièce jointe des mails du dossier configuré.
                    </p>
                </div>
                {!configured ? (
                    <div className="text-sm text-muted-foreground bg-muted/40 rounded-md px-4 py-3">
                        Configure d&apos;abord la connexion IMAP au-dessus.
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            onClick={runImport}
                            disabled={running}
                            className="bg-primary hover:bg-primary/90"
                            data-testid="imap-run-import"
                        >
                            {running ? "Import en cours…" : "Démarrer l'import"}
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={testRead}
                            disabled={testing || running}
                            data-testid="imap-test-read"
                        >
                            {testing ? "Test…" : "Tester la lecture"}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                            Dossier : <code className="bg-muted px-1.5 py-0.5 rounded">{divers?.folder || "INBOX"}</code>
                        </span>
                    </div>
                )}
                {probe && (
                    <div className="border border-border rounded-md p-3 bg-muted/30 text-sm space-y-2"
                         data-testid="imap-probe-result">
                        <div className="flex flex-wrap gap-3 text-xs">
                            <span className="font-semibold">{probe.total} mails au total</span>
                            <span className="text-muted-foreground">·</span>
                            <span>{probe.pdf_count_in_sample} PDF dans les {probe.sample_size} derniers mails</span>
                        </div>
                        {probe.samples?.length > 0 && (
                            <div className="space-y-1">
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                                    Derniers mails
                                </div>
                                {probe.samples.map((s, i) => (
                                    <div key={i}
                                         className="text-xs py-1.5 border-t border-border/60">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium truncate">{s.subject || "(sans sujet)"}</div>
                                                <div className="text-muted-foreground truncate">
                                                    {s.date} · {s.sender}
                                                </div>
                                            </div>
                                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium
                                                    ${s.pdf_attachments > 0 ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                                                {s.pdf_attachments} PDF
                                            </span>
                                        </div>
                                        {s.pdf_attachments === 0 && s.mime_parts?.length > 0 && (
                                            <details className="mt-1.5 text-[10px] text-muted-foreground">
                                                <summary className="cursor-pointer hover:text-foreground">
                                                    Voir les pièces MIME ({s.mime_parts.length})
                                                </summary>
                                                <div className="mt-1 space-y-0.5 font-mono pl-2 border-l border-border">
                                                    {s.mime_parts.map((mp, j) => (
                                                        <div key={j} className="truncate" title={mp}>{mp}</div>
                                                    ))}
                                                </div>
                                            </details>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {status && (
                    <div className="border-l-2 border-primary pl-3 py-1 text-sm" data-testid="imap-status">
                        <div className="font-medium">{status.message || status.status}</div>
                        {typeof status.progress === "number" && status.progress < 100 && (
                            <div className="text-xs text-muted-foreground mt-1">
                                Progression : {status.progress}%
                            </div>
                        )}
                        {status.invoices_found > 0 && (
                            <div className="text-xs text-emerald-700 mt-1">
                                {status.invoices_found} factures importées
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default function MailConnector() {
    return (
        <div className="p-6 space-y-6 max-w-5xl" data-testid="mail-connector-page">
            <div>
                <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
                    <Envelope size={32} weight="duotone" className="text-primary" />
                    Connecteur mail
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Récupère automatiquement les factures (PDF) depuis ta boîte mail IMAP.
                </p>
                <div className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-300 text-xs text-emerald-800"
                     data-testid="auto-sync-badge">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Synchronisation automatique activée — toutes les heures, sur les 7 derniers jours
                </div>
            </div>

            <ImapConfigCard />
            <ImportRunner />

            <div className="pt-2 border-t border-border">
                <div className="flex items-center gap-2 mb-3 mt-4">
                    <MagicWand size={18} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-xl font-bold">Mapping expéditeur → fournisseur</h2>
                </div>
                <VendorMappingsCard />
            </div>
        </div>
    );
}
